import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
    APP_INITIALIZER,
    ENVIRONMENT_INITIALIZER,
    EnvironmentProviders,
    Provider,
    importProvidersFrom,
    inject,
    makeEnvironmentProviders,
} from '@angular/core';
import { MATERIAL_SANITY_CHECKS } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { FUSE_MOCK_API_DEFAULT_DELAY, mockApiInterceptor } from '@fuse/lib/mock-api';
import { FuseConfig } from '@fuse/services/config';
import { FUSE_CONFIG } from '@fuse/services/config/config.constants';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { FuseLoadingService, fuseLoadingInterceptor } from '@fuse/services/loading';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FusePlatformService } from '@fuse/services/platform';
import { FuseSplashScreenService } from '@fuse/services/splash-screen';
import { FuseUtilsService } from '@fuse/services/utils';

export type FuseProviderConfig = {
    mockApi?: {
        delay?: number;
        service?: any;
    };
    fuse?: FuseConfig;
};

/**
 * Fuse provider
 */
export const provideFuse = (config: FuseProviderConfig): Array<Provider | EnvironmentProviders> => {
    // Base providers
    const providers: Array<Provider | EnvironmentProviders> = [
        {
            // Disable 'theme' sanity check
            provide: MATERIAL_SANITY_CHECKS,
            useValue: {
                doctype: true,
                theme: false,
                version: true,
            },
        },
        {
            // Use the 'fill' appearance on Angular Material form fields by default
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                appearance: 'fill',
            },
        },
        {
            provide: FUSE_MOCK_API_DEFAULT_DELAY,
            useValue: config?.mockApi?.delay ?? 0,
        },
        {
            provide: FUSE_CONFIG,
            useValue: config?.fuse ?? {},
        },

        importProvidersFrom(MatDialogModule),
        envInit(() => inject(FuseConfirmationService)),

        provideHttpClient(withInterceptors([fuseLoadingInterceptor])),
        envInit(() => inject(FuseLoadingService)),

        envInit(() => inject(FuseMediaWatcherService)),
        envInit(() => inject(FusePlatformService)),
        envInit(() => inject(FuseSplashScreenService)),
        envInit(() => inject(FuseUtilsService)),
    ];

    // Mock Api services
    if (config?.mockApi?.service) {
        providers.push(
            // 1.  add the interceptor so /api/** hits the mock layer
            provideHttpClient(withInterceptors([mockApiInterceptor])),

            // 2.  eager-instantiate the mock service at app start
            {
                provide: APP_INITIALIZER,
                // Angular passes the service as an argument → no inject() needed
                useFactory: (mockApiService: InstanceType<typeof config.mockApi.service>) => () => {
                    /* constructor already registered endpoints */
                },
                deps: [config.mockApi.service],
                multi: true,
            } as Provider
        );
    }

    // Return the providers
    return providers;
};

function envInit(fn: () => void) {
    return makeEnvironmentProviders([{ provide: ENVIRONMENT_INITIALIZER, useValue: fn, multi: true }]);
}

function appInit(fn: () => void) {
    return makeEnvironmentProviders([{ provide: APP_INITIALIZER, useValue: fn, multi: true }]);
}
